import React, { useEffect, useState, useCallback } from 'react';
import { FileText, FileImage, Download, Eye, Loader2, FolderOpen, ChevronDown, ChevronUp, ScanLine, Sparkles } from 'lucide-react';
import { api, useAuth } from '../context/AuthContext';
import { FilePreviewModal, isImage, isPdf } from './FilePreviewModal';
import { KiAuswertung } from './kanzlei/Ki';
import { AnMandantSenden } from './akte/AnMandantSenden';

const fmtSize = (b) => (!b ? '' : b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');

export const CaseDocuments = ({ caseId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const { user } = useAuth();
  const [ki, setKi] = useState(null);
  const [kiLaeuft, setKiLaeuft] = useState(null);
  const [kiFehler, setKiFehler] = useState('');
  const [neu, setNeu] = useState(0);
  const [gesendet, setGesendet] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/cases/${caseId}/documents`)
      .then((r) => alive && setItems(r.data.items || []))
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [caseId, neu]);

  const fetchBlob = useCallback(async (doc) => {
    const r = await api.get(doc.download_url, { responseType: 'blob' });
    return URL.createObjectURL(new Blob([r.data], { type: doc.content_type || r.headers['content-type'] }));
  }, []);

  const openPreview = async (doc) => {
    setPreview(doc);
    setPreviewUrl('');
    try { setPreviewUrl(await fetchBlob(doc)); } catch { setPreview(null); }
  };
  const closePreview = () => { if (previewUrl) URL.revokeObjectURL(previewUrl); setPreview(null); setPreviewUrl(''); };

  const download = async (doc) => {
    const url = await fetchBlob(doc);
    const a = document.createElement('a');
    a.href = url; a.download = doc.filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const portal = items.filter((d) => d.kind === 'portal');
  const anfrage = items.filter((d) => d.kind !== 'portal');
  const kiErlaubt = ['admin', 'kanzlei_mitarbeiter'].includes(user?.role);
  const auswerten = async (d) => {
    setKiLaeuft(d.id); setKiFehler('');
    try { const r = await api.post('/kanzlei/ki/dokument', { doc_id: d.id }, { timeout: 180000 }); setKi({ ...r.data, datei: d.filename }); } catch (e) { setKiFehler(e?.response?.data?.detail || 'KI-Auswertung fehlgeschlagen'); } finally { setKiLaeuft(null); }
  };

  const Row = ({ d }) => {
    const Icon = isImage(d.content_type) ? FileImage : FileText;
    const previewable = isImage(d.content_type) || isPdf(d.content_type);
    return (
      <div className="flex flex-wrap items-center gap-3 py-2.5 border-b border-gray-100 last:border-0" data-testid={`case-doc-${d.id}`}>
        <div className="w-9 h-9 bg-[#f5f8fa] rounded-sm flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-[#b8893d]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-[#1B3A5C] font-medium truncate">{d.filename}</p>
          <p className="text-[11px] text-[#34536b]/70">
            {[d.category, fmtSize(d.size), fmtDate(d.created_at), d.pages ? `${d.pages} S.` : null, d.source === 'scan' ? 'Foto-Scan' : null, d.source === 'esign' ? 'E-Signatur · beidseitig signiert' : null].filter(Boolean).join(' · ')}
          </p>
          {d.an_mandant?.[0] && <p className="text-[11px] text-emerald-700" data-testid={`case-doc-${d.id}-gesendet`}>An Mandanten gesendet am {fmtDate(d.an_mandant[0].am)} von {d.an_mandant[0].von} · {d.an_mandant[0].geoeffnet_am
            ? <b data-testid={`case-doc-${d.id}-geoeffnet`}>vom Mandanten geöffnet am {new Date(d.an_mandant[0].geoeffnet_am).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</b>
            : <span className="text-amber-700" data-testid={`case-doc-${d.id}-ungeoeffnet`}>noch nicht geöffnet</span>}</p>}
        </div>
        {previewable && (
          <button onClick={() => openPreview(d)} className="w-8 h-8 flex items-center justify-center text-[#1B3A5C] hover:bg-[#f5f8fa] rounded-sm" title="Vorschau" aria-label="Vorschau" data-testid={`case-doc-${d.id}-preview`}>
            <Eye className="w-4 h-4" />
          </button>
        )}
        {kiErlaubt && d.kind === 'portal' && previewable && (
          <button onClick={() => auswerten(d)} disabled={kiLaeuft === d.id} className="w-8 h-8 flex items-center justify-center text-[#b8893d] hover:bg-[#fbf7f0] rounded-sm" title="Mit KI auswerten (Frist, Behörde, Aktenzeichen)" aria-label="Mit KI auswerten" data-testid={`case-doc-${d.id}-ki`}>
            {kiLaeuft === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          </button>
        )}
        <button onClick={() => download(d)} className="w-8 h-8 flex items-center justify-center text-[#1B3A5C] hover:bg-[#f5f8fa] rounded-sm" title="Herunterladen" aria-label="Herunterladen" data-testid={`case-doc-${d.id}-download`}>
          <Download className="w-4 h-4" />
        </button>
        {kiErlaubt && isPdf(d.content_type) && <AnMandantSenden klein caseId={caseId} kind={d.kind === 'portal' ? 'portal' : 'anfrage'} docId={d.id} filename={d.filename} onGesendet={() => { setGesendet(`„${d.filename}“ wurde an den Mandanten gesendet.`); setNeu((n) => n + 1); }} />}
      </div>
    );
  };

  return (
    <div className="mb-5 pb-5 border-b border-gray-100" data-testid="case-documents">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left" data-testid="case-documents-toggle">
        <p className="text-[10px] tracking-[0.2em] text-[#b8893d] uppercase flex items-center gap-2">
          <FolderOpen className="w-3.5 h-3.5" /> Dokumente zur Akte {!loading && <span className="text-[#34536b]/60">({items.length})</span>}
        </p>
        {open ? <ChevronUp className="w-4 h-4 text-[#34536b]" /> : <ChevronDown className="w-4 h-4 text-[#34536b]" />}
      </button>
      {gesendet && <p className="mt-2 text-xs text-emerald-700" data-testid="case-doc-gesendet-meldung">{gesendet}</p>}
      {open && (
        <div className="mt-2">
          {loading ? (
            <Loader2 className="w-5 h-5 text-[#b8893d] animate-spin my-3" />
          ) : items.length === 0 ? (
            <p className="text-xs text-[#34536b]/70 py-2">Noch keine Dokumente zu dieser Akte.</p>
          ) : (
            <>
              {portal.length > 0 && (
                <div className="mb-2">
                  <p className="text-[10px] tracking-[0.15em] text-[#34536b]/60 flex items-center gap-1 mt-1"><ScanLine className="w-3 h-3" /> MANDANTENPORTAL</p>
                  {portal.map((d) => <Row key={d.id} d={d} />)}
                </div>
              )}
              {anfrage.length > 0 && (
                <div>
                  <p className="text-[10px] tracking-[0.15em] text-[#34536b]/60 mt-1">MANDANTENERFASSUNG</p>
                  {anfrage.map((d) => <Row key={d.id} d={d} />)}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {kiFehler && <p className="text-xs text-rose-700 mt-2" data-testid="case-doc-ki-fehler">{kiFehler}</p>}
      {ki && <KiAuswertung e={ki} onClose={() => setKi(null)} />}
      {preview && <FilePreviewModal filename={preview.filename} contentType={preview.content_type} url={previewUrl} onClose={closePreview} onDownload={() => download(preview)} testId="case-doc-preview" />}
    </div>
  );
};
